// The prompt that remixes a published game. It is any coding agent's: it names
// no plugin, no command of the Studio's and no link code, and the remix guide it
// points at says the rest. Nothing here imports the DOM.

import { DEFAULT_LANG, localePath, translate, type Lang } from "./i18n.ts";
import type { AppKind } from "./types.ts";

/**
 * What the user replaces in a prompt, by language. The skill asks for the game
 * when one of these is still there (plugins/pocket-studio/skills/make-game/SKILL.md).
 */
export const GAME_PLACEHOLDERS: Record<Lang, string> = { en: "<describe your game>", ja: "<ゲームの内容を書いてください>" };

/** The remix guide's page on the Studio. A remix prompt sends the agent to one of its parts. */
export const REMIX_GUIDE = "/docs/remix/";
/** The source and the documents of the engine the games built outside Pocket Studio run on. */
export const POCKET3D_SOURCE = "https://github.com/pocket-nexus/pocketjs/tree/main/engine/pocket3d";

/** A part of the remix guide in `lang`, as an address on the Studio at `origin`: how a remix is published, or how a Pocket app is copied. */
export const remixGuide = (origin: string, lang: Lang, part: "publish" | "pocket-app"): string =>
  `${origin.replace(/\/+$/, "")}${localePath(lang, REMIX_GUIDE)}#${part}`;

/** What a remix prompt says of the game: `page` is its page on the Studio, `repository` the public source of a game built outside Pocket Studio. */
export interface Remixed {
  kind: AppKind;
  id: string;
  title: string;
  page: string;
  repository?: string | null;
}

/**
 * The prompt that asks a coding agent to remix a published game, in `lang`.
 * It is the same for every agent and names nothing the computer has to hold
 * beforehand.
 *
 * - A game built outside Pocket Studio: the agent clones its public
 *   repository, builds it with the repository's own tools and changes it. The
 *   `pocket-studio` command comes in where the remix is published, which the
 *   guide's `#publish` part says.
 * - A Pocket app: the Studio holds its source for its accounts, so the agent
 *   follows the guide's `#pocket-app` part: it sets the computer up, links it
 *   with a code it asks the user for, and copies the game with
 *   `pocket-studio remix <id>`.
 */
export function remixPrompt(game: Remixed, origin: string, lang: Lang = DEFAULT_LANG): string {
  const said = { title: game.title, page: game.page, game: GAME_PLACEHOLDERS[lang] };
  return game.kind === "site" && game.repository
    ? translate(lang, "remix.prompt", { ...said, repository: game.repository, guide: remixGuide(origin, lang, "publish") })
    : translate(lang, "remix.prompt.pocket", { ...said, id: game.id, guide: remixGuide(origin, lang, "pocket-app") });
}
